const parseHexColor = (color: string): [number, number, number, number] | null => {
  const normalized = color.trim()

  if (normalized.startsWith('#')) {
    const hex = normalized.slice(1)

    if (hex.length === 3) {
      return [
        Number.parseInt(hex[0] + hex[0], 16),
        Number.parseInt(hex[1] + hex[1], 16),
        Number.parseInt(hex[2] + hex[2], 16),
        255,
      ]
    }

    if (hex.length === 6) {
      return [
        Number.parseInt(hex.slice(0, 2), 16),
        Number.parseInt(hex.slice(2, 4), 16),
        Number.parseInt(hex.slice(4, 6), 16),
        255,
      ]
    }

    if (hex.length === 8) {
      return [
        Number.parseInt(hex.slice(0, 2), 16),
        Number.parseInt(hex.slice(2, 4), 16),
        Number.parseInt(hex.slice(4, 6), 16),
        Number.parseInt(hex.slice(6, 8), 16),
      ]
    }
  }

  const rgbaMatch = normalized.match(/^rgba?\(([^)]+)\)$/i)

  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(',').map((part) => Number.parseFloat(part.trim()))
    const [red = 0, green = 0, blue = 0, alpha = 1] = parts
    return [red, green, blue, alpha <= 1 ? alpha * 255 : alpha]
  }

  return null
}

const mixChannel = (from: number, to: number, progress: number) => Math.round(from + (to - from) * progress)

const toColorString = ([red, green, blue, alpha]: [number, number, number, number]) => {
  if (alpha >= 255) {
    const toHex = (value: number) => value.toString(16).padStart(2, '0')
    return `#${toHex(red)}${toHex(green)}${toHex(blue)}`
  }

  return `rgba(${red}, ${green}, ${blue}, ${(alpha / 255).toFixed(3)})`
}

export function interpolateColor(value: number, inputRange: number[], outputRange: string[]): string {
  if (outputRange.length === 0) {
    return 'transparent'
  }

  if (outputRange.length === 1 || inputRange.length === 1) {
    return outputRange[0] ?? 'transparent'
  }

  const firstInput = inputRange[0] ?? 0
  const lastInput = inputRange[inputRange.length - 1] ?? 1
  const clampedValue = Math.min(lastInput, Math.max(firstInput, value))

  let segmentIndex = 0

  for (let index = 0; index < inputRange.length - 1; index += 1) {
    if (clampedValue <= (inputRange[index + 1] ?? lastInput)) {
      segmentIndex = index
      break
    }
  }

  const startInput = inputRange[segmentIndex] ?? firstInput
  const endInput = inputRange[segmentIndex + 1] ?? lastInput
  const progress = endInput === startInput ? 0 : (clampedValue - startInput) / (endInput - startInput)
  const fromColor = parseHexColor(outputRange[segmentIndex] ?? outputRange[0]) ?? [0, 0, 0, 255]
  const toColor =
    parseHexColor(outputRange[segmentIndex + 1] ?? outputRange[segmentIndex] ?? outputRange[0]) ?? fromColor

  return toColorString([
    mixChannel(fromColor[0], toColor[0], progress),
    mixChannel(fromColor[1], toColor[1], progress),
    mixChannel(fromColor[2], toColor[2], progress),
    mixChannel(fromColor[3], toColor[3], progress),
  ])
}
